import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { fetchPreviewMesh } from '../utils/igesExporter';

const LOOK = {
  background: '#e9ecef',
  floor: '#d5d9de',
  env: 0.85,
  rim: '#f4f6f8',
  sky: '#f7f8fa',
  ground: '#c5cad0',
  ink: '#3c4248',
  panel: 'rgba(255,255,255,0.84)',
  steps: [
    { top: '#d9363f', front: '#c42c35', side: '#9f222b' },
    { top: '#e5484f', front: '#d63a43', side: '#ad2a33' },
    { top: '#f58a8e', front: '#ef6a70', side: '#cc4a52' },
  ],
};

function roundedDeckShape(length, depth, radius) {
  const r = Math.min(radius, length * 0.2, depth * 0.2);
  const x = -length / 2;
  const y = -depth / 2;
  const shape = new THREE.Shape();
  shape.moveTo(x + r, y);
  shape.lineTo(x + length - r, y);
  shape.absarc(x + length - r, y + r, r, -Math.PI / 2, 0, false);
  shape.lineTo(x + length, y + depth - r);
  shape.absarc(x + length - r, y + depth - r, r, 0, Math.PI / 2, false);
  shape.lineTo(x + r, y + depth);
  shape.absarc(x + r, y + depth - r, r, Math.PI / 2, Math.PI, false);
  shape.lineTo(x, y + r);
  shape.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
  return shape;
}

function paintStep(geometry, colors, alongX) {
  const top = new THREE.Color(colors.top);
  const front = new THREE.Color(colors.front);
  const side = new THREE.Color(colors.side);
  const edge = colors.edge ? new THREE.Color(colors.edge) : null;
  const geo = geometry.toNonIndexed();
  geometry.dispose();
  const pos = geo.getAttribute('position');
  const colorAttr = new Float32Array(pos.count * 3);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const ab = new THREE.Vector3();
  const ac = new THREE.Vector3();
  const n = new THREE.Vector3();
  for (let i = 0; i < pos.count; i += 3) {
    a.fromBufferAttribute(pos, i);
    b.fromBufferAttribute(pos, i + 1);
    c.fromBufferAttribute(pos, i + 2);
    n.crossVectors(ab.subVectors(b, a), ac.subVectors(c, a));
    const spanN = n.length() || 1;
    const ax = Math.abs(n.x);
    const ay = Math.abs(n.y);
    const az = Math.abs(n.z);
    const endAxis = alongX ? ax : az;
    const longAxis = alongX ? az : ax;
    const slant = ay / spanN;
    let paint = front;
    if (edge && slant > 0.22 && slant < 0.92 && endAxis <= longAxis * 1.4) paint = edge;
    else if (ay >= endAxis && ay >= longAxis) paint = top;
    else if (endAxis >= longAxis) paint = side;
    for (let k = 0; k < 3; k += 1) {
      colorAttr[(i + k) * 3] = paint.r;
      colorAttr[(i + k) * 3 + 1] = paint.g;
      colorAttr[(i + k) * 3 + 2] = paint.b;
    }
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colorAttr, 3));
  geo.computeVertexNormals();
  return geo;
}

function stepGeometry(length, depth, height, cornerRadius, alongX, colors) {
  const bevel = Math.min(1.7, height * 0.06);
  const geometry = new THREE.ExtrudeGeometry(roundedDeckShape(length, depth, cornerRadius), {
    depth: Math.max(height - bevel * 2, bevel),
    bevelEnabled: true,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 2,
    curveSegments: 10,
  });
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  geometry.translate(0, 0, -(box.min.z + box.max.z) / 2);
  geometry.rotateX(-Math.PI / 2);
  if (!alongX) geometry.rotateY(Math.PI / 2);
  const shear = Math.tan(THREE.MathUtils.degToRad(12));
  geometry.applyMatrix4(alongX
    ? new THREE.Matrix4().makeShear(0, 0, shear, 0, 0, 0)
    : new THREE.Matrix4().makeShear(0, 0, 0, shear, 0, 0));
  geometry.computeBoundingBox();
  const mid = geometry.boundingBox.getCenter(new THREE.Vector3());
  geometry.translate(-mid.x, -geometry.boundingBox.min.y - height / 2, -mid.z);
  return paintStep(geometry, colors, alongX);
}

function productMaterial(name, envMap) {
  if (name === 'plastic') {
    return new THREE.MeshStandardMaterial({
      color: 0x141414,
      metalness: 0.02,
      roughness: 0.62,
      envMapIntensity: 0.25,
    });
  }
  if (name === 'brass') {
    return new THREE.MeshStandardMaterial({
      color: 0xc6a14c,
      metalness: 0.9,
      roughness: 0.3,
      envMapIntensity: 1.05,
    });
  }
  return new THREE.MeshStandardMaterial({
    color: 0xc5cad0,
    metalness: 0.96,
    roughness: 0.18,
    envMap,
    envMapIntensity: 1.45,
  });
}

function mountPreview(container, mesh) {
  const width = container.clientWidth || 800;
  const height = container.clientHeight || 560;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.domElement.style.display = 'block';
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  renderer.setSize(width, height, false);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.95;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(LOOK.background);
  scene.environmentIntensity = LOOK.env;

  const pmrem = new THREE.PMREMGenerator(renderer);
  const room = new RoomEnvironment();
  const environment = pmrem.fromScene(room, 0.04).texture;
  scene.environment = environment;

  const camera = new THREE.PerspectiveCamera(30, width / height, 1, 200000);
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.minPolarAngle = 0.12;
  controls.maxPolarAngle = Math.PI / 2 + 0.22;

  const groups = mesh.groups?.length
    ? mesh.groups
    : [{ material: 'steel', positions: mesh.positions, normals: mesh.normals, indices: mesh.indices }];
  const geometries = [];
  const materials = [];
  const bounds = new THREE.Box3();
  groups.forEach((group) => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(group.positions, 3));
    geometry.setIndex(group.indices);
    if (group.normals && group.normals.length === group.positions.length) {
      geometry.setAttribute('normal', new THREE.Float32BufferAttribute(group.normals, 3));
    } else {
      geometry.computeVertexNormals();
    }
    geometry.computeBoundingBox();
    bounds.union(geometry.boundingBox);
    geometries.push(geometry);
  });
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  bounds.getCenter(center);
  bounds.getSize(size);
  geometries.forEach((geometry, index) => {
    geometry.translate(-center.x, -bounds.min.y, -center.z);
    const material = productMaterial(groups[index].material, environment);
    const product = new THREE.Mesh(geometry, material);
    product.castShadow = true;
    product.receiveShadow = false;
    scene.add(product);
    materials.push(material);
  });

  const span = Math.max(size.x, size.y, size.z, 80);
  const alongX = size.x >= size.z;
  const topX = size.x * 1.15;
  const topZ = size.z + Math.max(size.z * 1.1, 240);
  const overhangX = topX * 0.065;
  const overhangZ = topZ * 0.065;
  const cornerRadius = Math.max(topX, topZ) * 0.03;
  const heights = [36, 54, 72];
  const gap = 2.4;
  const floorGap = 8;
  const disposables = [];
  const footprints = [0, 1, 2].map((tier) => {
    const worldX = topX + overhangX * 2 * (2 - tier);
    const worldZ = topZ + overhangZ * 2 * (2 - tier);
    return {
      length: alongX ? worldX : worldZ,
      depth: alongX ? worldZ : worldX,
      worldX,
      worldZ,
    };
  });
  let cursor = 0;
  for (let tier = 2; tier >= 0; tier -= 1) {
    const stepHeight = heights[tier];
    const deck = footprints[tier];
    const raw = stepGeometry(deck.length, deck.depth, stepHeight, cornerRadius, alongX, LOOK.steps[tier]);
    const roughness = tier === 2 ? 0.35 : 0.55;
    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness,
      metalness: 0,
      envMapIntensity: 0.18,
    });
    const meshStep = new THREE.Mesh(raw, material);
    meshStep.position.y = cursor - stepHeight / 2;
    meshStep.castShadow = true;
    meshStep.receiveShadow = true;
    scene.add(meshStep);
    disposables.push(raw, material);
    cursor -= stepHeight;
    if (tier > 0) cursor -= gap;
  }

  const floorSize = Math.max(span * 40, 80000);
  const floorGeometry = new THREE.PlaneGeometry(floorSize, floorSize);
  const floorMaterial = new THREE.MeshStandardMaterial({
    color: LOOK.floor,
    roughness: 0.72,
    metalness: 0,
    envMapIntensity: 0.08,
  });
  const floor = new THREE.Mesh(floorGeometry, floorMaterial);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = cursor - floorGap;
  floor.receiveShadow = true;
  scene.add(floor);
  disposables.push(floorGeometry, floorMaterial);

  scene.fog = new THREE.Fog(LOOK.background, span * 3.4, span * 10);

  const key = new THREE.DirectionalLight(0xf4f6f8, 1.25);
  key.position.set(span * 0.95, span * 1.7, span * 0.55);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.radius = 4;
  key.shadow.bias = -0.0004;
  key.shadow.normalBias = 0.8;
  key.shadow.camera.near = 10;
  key.shadow.camera.far = span * 8;
  const shadowReach = Math.max(footprints[0].length, footprints[0].depth) * 0.85;
  key.shadow.camera.left = -shadowReach;
  key.shadow.camera.right = shadowReach;
  key.shadow.camera.top = shadowReach;
  key.shadow.camera.bottom = -shadowReach;
  scene.add(key);

  const rim = new THREE.DirectionalLight(LOOK.rim, 0.35);
  rim.position.set(-span * 0.8, span * 0.65, -span * 1.05);
  scene.add(rim);
  scene.add(new THREE.HemisphereLight(LOOK.sky, LOOK.ground, 0.42));

  const lookY = Math.max(size.y * 0.46, 40);
  const floorLimit = cursor - floorGap + 24;
  const fit = Math.max(topX, topZ, size.y) * 1.05;
  const distance = (fit / (2 * Math.tan(THREE.MathUtils.degToRad(15)))) * 1.18;
  const yaw = THREE.MathUtils.degToRad(32);
  camera.position.set(
    Math.sin(yaw) * distance * 0.38,
    Math.max(lookY - size.y * 0.12, floorLimit),
    Math.cos(yaw) * distance,
  );
  controls.target.set(0, lookY, 0);
  controls.minDistance = span * 0.35;
  controls.maxDistance = span * 14;
  controls.update();

  let frame = 0;
  const draw = () => {
    frame = requestAnimationFrame(draw);
    controls.update();
    if (camera.position.y < floorLimit) camera.position.y = floorLimit;
    renderer.render(scene, camera);
  };
  draw();

  let drawnWidth = 0;
  let drawnHeight = 0;
  let resizeFrame = 0;
  const fitView = () => {
    const nextWidth = container.clientWidth;
    const nextHeight = container.clientHeight;
    if (nextWidth < 2 || nextHeight < 2) return;
    if (nextWidth === drawnWidth && nextHeight === drawnHeight) return;
    drawnWidth = nextWidth;
    drawnHeight = nextHeight;
    camera.aspect = nextWidth / nextHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(nextWidth, nextHeight, false);
  };
  const onResize = () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(fitView);
  };
  const observer = new ResizeObserver(onResize);
  observer.observe(container);
  fitView();

  return () => {
    cancelAnimationFrame(frame);
    cancelAnimationFrame(resizeFrame);
    observer.disconnect();
    controls.dispose();
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
    disposables.forEach((item) => item.dispose());
    environment.dispose();
    room.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        const list = Array.isArray(child.material) ? child.material : [child.material];
        list.forEach((item) => item.dispose());
      }
    });
    pmrem.dispose();
    renderer.dispose();
    if (renderer.domElement.parentNode === container) {
      container.removeChild(renderer.domElement);
    }
  };
}

export default function ModelPreviewDialog({ open, onClose, getDrawing }) {
  const viewRef = useRef(null);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [mesh, setMesh] = useState(null);

  useEffect(() => {
    if (!open) {
      setPhase('idle');
      setError('');
      setMesh(null);
      return undefined;
    }
    let cancelled = false;
    setPhase('loading');
    setError('');
    setMesh(null);
    (async () => {
      try {
        const drawing = getDrawing?.();
        if (!drawing) throw new Error('Нет данных чертежа');
        const payload = await fetchPreviewMesh(drawing);
        if (!cancelled) {
          setMesh(payload);
          setPhase('ready');
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Не удалось построить модель');
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, getDrawing]);

  useEffect(() => {
    if (!open || phase !== 'ready' || !mesh || !viewRef.current) return undefined;
    return mountPreview(viewRef.current, mesh);
  }, [open, phase, mesh]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 'min(1100px, 94vw)',
          height: 'min(760px, 90vh)',
          m: 2,
          borderRadius: '0.75rem',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', height: '100%', background: LOOK.background }}>
        <Box ref={viewRef} sx={{ width: '100%', height: '100%' }} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(ellipse at center, transparent 62%, rgba(0,0,0,0.13) 100%)',
          }}
        />
        <IconButton
          onClick={onClose}
          aria-label="Закрыть"
          sx={{
            position: 'absolute', top: 8, right: 8, color: LOOK.ink,
            background: LOOK.panel,
            '&:hover': { background: LOOK.panel },
          }}
        >
          <CloseIcon />
        </IconButton>
        {phase === 'loading' && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, background: LOOK.background,
          }}>
            <CircularProgress sx={{ color: '#d32f2f' }} />
            <Typography sx={{ color: LOOK.ink, fontSize: '0.95rem' }}>
              Собираем изделие…
            </Typography>
          </Box>
        )}
        {phase === 'error' && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', px: 4, background: LOOK.background,
          }}>
            <Typography sx={{ color: '#b71c1c', textAlign: 'center', maxWidth: 460 }}>
              {error}
            </Typography>
          </Box>
        )}
        {phase === 'ready' && (
          <Typography sx={{
            position: 'absolute', left: 16, bottom: 14, color: LOOK.ink,
            fontSize: '0.78rem', pointerEvents: 'none', opacity: 0.8,
          }}>
            Левая кнопка — вращение, колёсико — масштаб, правая — сдвиг
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
